/**
 * useClientAggregationBacktest – client-side aggregation single-backtest hook.
 *
 * Fetches candles (and optional funding rates / OI / LSR) for each sub-strategy
 * symbol from the REST API, loads strategy source code via getUserStrategies,
 * then delegates the full aggregation backtest to a Web Worker.
 *
 * Results are written into the shared Zustand backtest store so existing
 * dashboard panels render them automatically, and persisted via submitClientBacktest.
 */

import { useState, useCallback, useRef } from 'react';
import { useBacktestStore } from '../stores/backtestStore.js';
import {
  getCandles,
  getFundingRates,
  getOpenInterest as fetchOpenInterest,
  getLongShortRatio as fetchLongShortRatio,
  submitClientBacktest,
  getUserStrategies,
  getUserStrategyVersion,
} from '../api/client.js';
import type {
  AggregationBacktestInput,
  AggregationBacktestResult,
  WorkerMessage,
  SymbolMarketData,
} from '../workers/backtest-worker-types.js';
import type { Timeframe } from '../types.js';
import { WARMUP_BARS } from '../../core/constants.js';
import { timeframeToMs } from '../../core/types.js';
import { executableStrategyCode } from '../utils/executable-strategy-code.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface AggregationBacktestProgress {
  current: number;
  total: number;
  percent: number;
}

/** Sub-strategy descriptor passed to the hook. */
export interface AggregationSubStrategy {
  strategyName: string;
  symbol: string;
  timeframe: Timeframe;
  params?: Record<string, unknown>;
  exchange?: string;
}

/** Input to `useClientAggregationBacktest().run()`. */
export interface ClientAggregationBacktestInput {
  subStrategies: AggregationSubStrategy[];
  allocationMode: 'single_strongest' | 'weighted_multi' | 'top_n';
  maxPositions: number;
  initialCapital: number;
  /** ISO date string, e.g. "2024-01-01" */
  startDate: string;
  /** ISO date string, e.g. "2024-06-01" */
  endDate: string;
  exchange: string;
  /**
   * Shared params applied (on top of each sub-strategy's saved params) to ALL sub-strategies.
   * Provide either this OR perSubParams, not both.
   */
  sharedParams?: Record<string, unknown>;
  /**
   * Per-sub-strategy param overrides (index-aligned with subStrategies).
   * Provide either this OR sharedParams, not both.
   */
  perSubParams?: Array<Record<string, unknown>>;
  feeRate?: number;
  slippagePercent?: number;
  leverage?: number;
  /** Name to store in the persisted result. Defaults to "aggregation-backtest". */
  aggregationName?: string;
  /**
   * Pinned strategy version numbers — index-aligned with `subStrategies`.
   * When set, the hook fetches the PINNED version code for each sub via
   * GET /api/user-strategies/by-name/:name/versions/:version (BUG-20 fix).
   * When not set (or entry is undefined), falls back to the live strategy code.
   */
  subStrategyVersions?: number[];
  /**
   * Phase 2: the aggregation_config id for this run (when pre-created by the server).
   * Passed through to submitClientBacktest for persistence linking.
   */
  aggregationId?: string;
  /**
   * CP-1: the strategy_config id for single-strategy agg-of-one runs (dashboard).
   * When present, the server links the MULTI result to the aof1- aggregation record
   * so it buckets under 'strategies' (not 'aggregations') in the history view.
   */
  strategyConfigId?: string;
}

/** Options for the hook. */
export interface ClientAggregationBacktestOptions {
  /** Worker execution timeout in milliseconds. Defaults to 120 000 ms (2 min). */
  timeoutMs?: number;
}

interface HookState {
  running: boolean;
  progress: AggregationBacktestProgress | null;
  error: string | null;
}

const DEFAULT_TIMEOUT_MS = 120_000;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useClientAggregationBacktest(options: ClientAggregationBacktestOptions = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const [state, setState] = useState<HookState>({
    running: false,
    progress: null,
    error: null,
  });

  const workerRef = useRef<Worker | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setResult, setRunning, setError: setStoreError } = useBacktestStore();

  const clearWorkerTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const run = useCallback(
    async (input: ClientAggregationBacktestInput): Promise<AggregationBacktestResult> => {
      // Terminate any existing worker before starting a new one
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      clearWorkerTimeout();

      setState({ running: true, progress: null, error: null });
      setRunning(true);

      const idempotencyKey = crypto.randomUUID();
      const startedAt = Date.now();
      const startDateMs = new Date(input.startDate).getTime();
      const endDateMs = new Date(input.endDate).getTime();

      let marketDataBySymbol: SymbolMarketData[] = [];
      let strategyCodes: Record<string, string> = {};

      try {
        // ── 1. Load strategy source codes ────────────────────────────────────
        // BUG-20 fix: when subStrategyVersions is provided, fetch PINNED version
        // code for each sub independently. When not provided (or entry is
        // undefined), fall back to live strategy code from getUserStrategies.
        const codeMap: Record<string, string> = {};

        if (input.subStrategyVersions && input.subStrategyVersions.length > 0) {
          // Version-aware path: iterate subs individually (not just unique names)
          // so that two subs with the same name at different versions are both
          // fetched. Last-writer-wins in codeMap for same name (acceptable trade-off
          // — most configs have each sub at the same version; the parity-trap case
          // is an edge case that warrants a future worker-interface upgrade).
          let lazyLive: Awaited<ReturnType<typeof getUserStrategies>> | null = null;

          for (let subIdx = 0; subIdx < input.subStrategies.length; subIdx++) {
            const sub = input.subStrategies[subIdx];
            if (!sub) continue;
            const pinnedVersion = input.subStrategyVersions[subIdx];

            if (pinnedVersion !== undefined && pinnedVersion !== null) {
              // Fetch pinned version from the version endpoint
              const record = await getUserStrategyVersion(sub.strategyName, pinnedVersion);
              if (record) {
                // Prefer compiledCode (already transpiled); fall back to raw code
                codeMap[sub.strategyName] = record.compiledCode || record.code;
                continue;
              }
              // Version not found (null) — loud fallback to live strategy
              console.warn(
                `[BUG-20] strategy_version_unpinned_fallback: ` +
                `"${sub.strategyName}" v${pinnedVersion} not found, using live code`,
              );
            }

            // No version pin or version not found — fetch live strategy
            if (!lazyLive) {
              lazyLive = await getUserStrategies();
            }
            const match = lazyLive.find((s) => s.name === sub.strategyName);
            if (!match) {
              throw new Error(
                `Strategy "${sub.strategyName}" not found in user strategies. It may have been deleted.`,
              );
            }
            codeMap[sub.strategyName] = executableStrategyCode(match);
          }
        } else {
          // No version pinning — original live-only path
          const userStrategies = await getUserStrategies();
          const uniqueStrategyNames = new Set(input.subStrategies.map((s) => s.strategyName));

          for (const strategyName of uniqueStrategyNames) {
            const match = userStrategies.find((s) => s.name === strategyName);
            if (!match) {
              throw new Error(
                `Strategy "${strategyName}" not found in user strategies. It may have been deleted.`,
              );
            }
            codeMap[strategyName] = executableStrategyCode(match);
          }
        }
        strategyCodes = codeMap;

        // ── 2. Fetch market data for each unique symbol ───────────────────────
        // Deduplicate by "symbol+exchange+timeframe" so we don't double-fetch
        const uniqueSymbolKeys = new Map<
          string,
          { symbol: string; exchange: string; timeframe: Timeframe }
        >();

        for (const sub of input.subStrategies) {
          const key = `${sub.symbol}:${sub.exchange ?? input.exchange}`;
          if (!uniqueSymbolKeys.has(key)) {
            uniqueSymbolKeys.set(key, {
              symbol: sub.symbol,
              exchange: sub.exchange ?? input.exchange,
              timeframe: sub.timeframe,
            });
          }
        }

        const symbolDataResults = await Promise.all(
          Array.from(uniqueSymbolKeys.values()).map(async ({ symbol, exchange, timeframe }) => {
            // Extend the fetch start by WARMUP_BARS candles so indicators are primed
            // before trading begins at startDateMs. The worker/engine only trades bars
            // >= config.startDate so warmup candles prime indicators only.
            const warmupFetchStart = new Date(
              startDateMs - WARMUP_BARS * timeframeToMs(timeframe),
            ).toISOString();

            const candles = await getCandles({
              exchange,
              symbol,
              timeframe,
              startDate: warmupFetchStart,
              endDate: input.endDate,
            });

            // Empty candles: return a SymbolMarketData with an empty candle array.
            // SHA-19: the worker now FAILS LOUD (throws, naming the missing symbol)
            // the moment it finds ANY sub-strategy with no candle data — it no longer
            // silently skips the sub and reallocates its capital across the rest
            // (Rule 11). We only short-circuit here (skip the worker round-trip
            // entirely) when ALL symbols came back empty — see the allEmpty check
            // below; any partial-empty case is still forwarded to the worker so it
            // can produce the single, precisely-worded fail-loud error.
            if (candles.length === 0) {
              const emptyMd: SymbolMarketData = {
                symbol,
                candles: [],
                fundingRates: [],
                openInterest: [],
                longShortRatios: [],
              };
              return emptyMd;
            }

            const frResponse = await getFundingRates({
              exchange,
              symbol,
              start: startDateMs,
              end: endDateMs,
            });

            const [oiResponse, lsrResponse] = await Promise.all([
              fetchOpenInterest({ exchange, symbol, start: startDateMs, end: endDateMs }).catch(
                () => ({ records: [] as Array<{ timestamp: number; openInterestAmount: number }> }),
              ),
              fetchLongShortRatio({ exchange, symbol, start: startDateMs, end: endDateMs }).catch(
                () => ({
                  records: [] as Array<{
                    timestamp: number;
                    longRatio: number;
                    shortRatio: number;
                    longShortRatio: number;
                  }>,
                }),
              ),
            ]);

            const md: SymbolMarketData = {
              symbol,
              candles,
              fundingRates: frResponse.rates,
              openInterest: oiResponse.records,
              longShortRatios: lsrResponse.records,
            };
            return md;
          }),
        );

        // Check if ALL symbols have empty candles — only then error out
        const allEmpty = symbolDataResults.every((md) => md.candles.length === 0);
        if (allEmpty) {
          const symbols = symbolDataResults.map((md) => md.symbol).join(', ');
          throw new Error(
            `No candles found for any sub-strategy (${symbols}). ` +
            `Cannot run aggregation backtest with no active sub-strategies.`,
          );
        }

        marketDataBySymbol = symbolDataResults;
      } catch (err) {
        clearWorkerTimeout();
        const errorMsg = err instanceof Error ? err.message : String(err);
        setStoreError(errorMsg);
        setState({ running: false, progress: null, error: errorMsg });
        throw err;
      }

      // ── 3. Build baseConfig for the worker ────────────────────────────────
      const baseConfig = {
        subStrategies: input.subStrategies.map((sub) => ({
          strategyName: sub.strategyName,
          symbol: sub.symbol,
          timeframe: sub.timeframe,
          params: sub.params ?? {},
          exchange: sub.exchange ?? input.exchange,
        })),
        allocationMode: input.allocationMode,
        maxPositions: input.maxPositions,
        initialCapital: input.initialCapital,
        startDate: startDateMs,
        endDate: endDateMs,
        exchange: input.exchange,
        feeRate: input.feeRate,
        slippagePercent: input.slippagePercent,
        leverage: input.leverage,
      };

      // ── 4. Wrap worker lifecycle in a Promise ─────────────────────────────
      return new Promise<AggregationBacktestResult>((resolve, reject) => {
        let settled = false;

        const worker = new Worker(
          new URL('../workers/backtest.worker.ts', import.meta.url),
          { type: 'module' },
        );
        workerRef.current = worker;

        // ── 5. Start timeout watchdog ─────────────────────────────────────
        timeoutRef.current = setTimeout(() => {
          if (settled) return;
          settled = true;
          const errorMsg = `Aggregation backtest timed out after ${Math.round(timeoutMs / 1000)}s`;
          worker.terminate();
          workerRef.current = null;
          timeoutRef.current = null;
          setStoreError(errorMsg);
          setState({ running: false, progress: null, error: errorMsg });
          reject(new Error(errorMsg));
        }, timeoutMs);

        worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
          const msg = e.data;

          // Handle progress message
          if (msg.type === 'aggregation_backtest_progress') {
            setState((prev) => ({
              ...prev,
              progress: {
                current: msg.current,
                total: msg.total,
                percent: msg.percent,
              },
            }));
            return;
          }

          // Ignore all other non-aggregation_backtest_result messages
          if (msg.type !== 'aggregation_backtest_result') {
            return;
          }

          if (settled) return;
          settled = true;

          clearWorkerTimeout();

          if (msg.success) {
            const aggResult = msg.result;

            // Build an ApiBacktestResult-compatible object for the store
            const aggregationLabel = input.aggregationName ?? 'aggregation-backtest';

            // Phase 3.5: single-asset runs (1 sub + strategyConfigId) must be stored
            // with their real symbol, not 'MULTI', so history renders a price chart
            // and the discriminator (strategyConfigId IS NOT NULL) is consistent.
            // Portfolio runs (aggregationId set OR >1 sub) keep symbol='MULTI'.
            const isSingleAsset =
              input.strategyConfigId != null && input.subStrategies.length === 1;
            const persistSymbol = isSingleAsset
              ? (input.subStrategies[0].symbol)
              : 'MULTI';

            const storeResult = {
              id: `browser-agg-${startedAt}`,
              config: {
                id: `browser-agg-config-${startedAt}`,
                strategyName: aggregationLabel,
                params: input.sharedParams ?? {},
                symbol: persistSymbol,
                timeframe: (input.subStrategies[0]?.timeframe ?? '4h') as Timeframe,
                startDate: startDateMs,
                endDate: endDateMs,
                initialCapital: input.initialCapital,
                exchange: input.exchange,
              },
              trades: aggResult.trades,
              metrics: aggResult.metrics,
              equity: aggResult.equity,
              candles: [],
              createdAt: startedAt,
              duration: Date.now() - startedAt,
              // perAssetResults forwarded for multi-asset dashboard panels
              perAssetResults: aggResult.perAssetResults,
              // Phase 3.5: carry strategyConfigId into the store result so rerun can thread it
              ...(isSingleAsset ? { strategyConfigId: input.strategyConfigId } : {}),
              // Forward skipped subs so the dashboard can surface a warning
              ...(aggResult.skippedSubStrategies !== undefined
                ? { skippedSubStrategies: aggResult.skippedSubStrategies }
                : {}),
            };

            setResult(storeResult);
            setState({ running: false, progress: null, error: null });

            // Persist to database (fire-and-forget — Rule 8)
            // Include perAssetResults/signalHistory so the unified read shape works.
            // aggregationId comes from the caller input when present (multi-asset run).
            submitClientBacktest(
              {
                id: storeResult.id,
                config: {
                  strategyName: aggregationLabel,
                  symbol: persistSymbol,
                  timeframe: storeResult.config.timeframe,
                  startDate: startDateMs,
                  endDate: endDateMs,
                  initialCapital: input.initialCapital,
                  exchange: input.exchange,
                  params: input.sharedParams ?? {},
                },
                trades: aggResult.trades,
                equity: aggResult.equity,
                metrics: aggResult.metrics as Record<string, unknown>,
                // Phase 2: persist perAssetResults and signalHistory for unified shape
                perAssetResults: aggResult.perAssetResults as Record<string, unknown>,
                signalHistory: aggResult.signalHistory as unknown[],
                // aggregationId comes from the caller if a config was pre-created
                ...(input.aggregationId != null ? { aggregationId: input.aggregationId } : {}),
                // CP-1: for single-strategy agg-of-one from the dashboard, link to the strategy config
                ...(input.strategyConfigId != null ? { strategyConfigId: input.strategyConfigId } : {}),
              },
              idempotencyKey,
            ).catch(() => {
              // Silently ignore save failures — result is already displayed
            });

            worker.terminate();
            workerRef.current = null;
            resolve(aggResult);
          } else {
            setStoreError(msg.error);
            setState({ running: false, progress: null, error: msg.error });
            worker.terminate();
            workerRef.current = null;
            reject(new Error(msg.error));
          }
        };

        worker.onerror = (e: ErrorEvent) => {
          if (settled) return;
          settled = true;
          clearWorkerTimeout();
          const errorMsg = e.message || 'Worker error';
          setStoreError(errorMsg);
          setState({ running: false, progress: null, error: errorMsg });
          worker.terminate();
          workerRef.current = null;
          reject(new Error(errorMsg));
        };

        // ── 6. Post input to worker ────────────────────────────────────────
        const workerInput: AggregationBacktestInput = {
          type: 'aggregation_backtest',
          baseConfig,
          strategyCodes,
          marketDataBySymbol,
          ...(input.sharedParams !== undefined ? { sharedParams: input.sharedParams } : {}),
          ...(input.perSubParams !== undefined ? { perSubParams: input.perSubParams } : {}),
        };

        worker.postMessage(workerInput);
      });
    },
    [setResult, setRunning, setStoreError, timeoutMs, clearWorkerTimeout],
  );

  const cancel = useCallback(() => {
    clearWorkerTimeout();
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    setStoreError('Cancelled');
    setState({ running: false, progress: null, error: 'Cancelled' });
  }, [setStoreError, clearWorkerTimeout]);

  return { ...state, run, cancel };
}
